// pages/UserOrdersListPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Fade,
  Grow,
  Skeleton,
  Alert,
  Avatar,
  Stack
} from '@mui/material';
import {
  ReceiptLong as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  LocalShipping as ShippingIcon,
  DeliveryDining as DeliveredIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as OrderIcon,
  KeyboardArrowRight as ArrowRightIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const UserOrdersListPage = ({ language }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load orders from localStorage on component mount
  useEffect(() => {
    loadOrders();
  }, []);

  // Load orders from localStorage
  const loadOrders = () => {
    setLoading(true);
    try {
      const storedOrders = localStorage.getItem('user_orders');
      if (storedOrders) {
        const parsedOrders = JSON.parse(storedOrders);
        // Sort orders by date (newest first)
        const sortedOrders = parsedOrders.sort((a, b) => 
          new Date(b.order_date) - new Date(a.order_date)
        );
        setOrders(sortedOrders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete a single order
  const deleteOrder = (orderId) => {
    if (window.confirm(language === 'ar' 
      ? 'هل أنت متأكد من حذف هذا الطلب؟'
      : 'Are you sure you want to delete this order?'
    )) {
      const updatedOrders = orders.filter(order => order.id !== orderId);
      setOrders(updatedOrders);
      localStorage.setItem('user_orders', JSON.stringify(updatedOrders));
    }
  };

  // Clear all orders
  const clearAllOrders = () => {
    if (window.confirm(language === 'ar'
      ? 'هل أنت متأكد من حذف جميع الطلبات؟'
      : 'Are you sure you want to clear all orders?'
    )) {
      setOrders([]);
      localStorage.removeItem('user_orders');
    }
  };

  // Navigate to order details
  const navigateToOrder = (orderId) => {
    navigate(`/order-confirmation/${orderId}`);
  };

  // Get status chip with icon and color
  const getStatusChip = (status) => {
    const statusMap = {
      'delivered': {
        label: language === 'ar' ? 'تم التسليم' : 'Delivered',
        icon: <DeliveredIcon sx={{ fontSize: 16 }} />,
        color: '#4CAF50',
        bgColor: 'rgba(76, 175, 80, 0.15)'
      },
      'processing': {
        label: language === 'ar' ? 'جاري المعالجة' : 'Processing',
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        color: '#FF9800',
        bgColor: 'rgba(255, 152, 0, 0.15)'
      },
      'canceled': {
        label: language === 'ar' ? 'ملغي' : 'Canceled',
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        color: '#F44336',
        bgColor: 'rgba(244, 67, 54, 0.15)'
      },
      'shipped': {
        label: language === 'ar' ? 'تم الشحن' : 'Shipped',
        icon: <ShippingIcon sx={{ fontSize: 16 }} />,
        color: '#2196F3',
        bgColor: 'rgba(33, 150, 243, 0.15)'
      },
      'pending': {
        label: language === 'ar' ? 'قيد الانتظار' : 'Pending',
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        color: '#FFC107',
        bgColor: 'rgba(255, 193, 7, 0.15)'
      }
    };
    
    const statusInfo = statusMap[status] || statusMap['processing'];
    
    return (
      <Chip
        icon={statusInfo.icon}
        label={statusInfo.label}
        size="small"
        sx={{
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.color,
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: statusInfo.color
          }
        }}
      />
    );
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const locale = language === 'ar' ? ar : enUS;
      return format(date, 'PPP', { locale });
    } catch {
      return dateString;
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const locale = language === 'ar' ? ar : enUS;
      return formatDistanceToNow(date, { addSuffix: true, locale });
    } catch {
      return dateString;
    }
  };

  // Format price
  const formatPrice = (amount, currency = 'MYR') => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      style: 'currency',
      currency: currency || 'MYR'
    }).format(amount);
  };

  // Mobile order card view
  const OrderCard = ({ order }) => (
    <Card
      onClick={() => navigateToOrder(order.id)}
      sx={{
        mb: 2,
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#FFD700',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(255, 215, 0, 0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header: Order ID + Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrderIcon sx={{ color: '#FFD700', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 700 }}>
              #{order.order_number || order.id}
            </Typography>
          </Box>
          {getStatusChip(order.status)}
        </Box>

        {/* Restaurant Name */}
        <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 0.5 }}>
          {language === 'ar' ? order.restaurant_name : order.restaurant_name_en}
        </Typography>

        {/* Items Summary */}
        <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1 }}>
          {order.items_count || order.items?.length || 0} {language === 'ar' ? 'منتجات' : 'items'}
        </Typography>

        {/* Total Amount */}
        <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
          {formatPrice(order.total_amount, order.currency)}
        </Typography>

        {/* Footer: Date + Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 1,
          pt: 1,
          borderTop: '1px solid #2D2D2D'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: '#6B6B6B', fontSize: 14 }} />
            <Typography variant="caption" sx={{ color: '#6B6B6B' }}>
              {formatDate(order.order_date)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#6B6B6B' }}>
              {formatTimeAgo(order.order_date)}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                deleteOrder(order.id);
              }}
              sx={{ color: '#F44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <ArrowRightIcon sx={{ color: '#FFD700', fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Desktop order row view
  const OrderRow = ({ order }) => (
    <Paper
      onClick={() => navigateToOrder(order.id)}
      sx={{
        mb: 1,
        p: 2,
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#FFD700',
          backgroundColor: '#1A1A1A'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
        <Box sx={{ minWidth: 120 }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.7rem' }}>
            {language === 'ar' ? 'رقم الطلب' : 'Order #'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 700 }}>
            #{order.order_number || order.id}
          </Typography>
        </Box>
        
        
        <Box sx={{ minWidth: 100 }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.7rem' }}>
            {language === 'ar' ? 'التاريخ' : 'Date'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
            {formatDate(order.order_date)}
          </Typography>
        </Box>
        
        <Box sx={{ minWidth: 80 }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.7rem' }}>
            {language === 'ar' ? 'الحالة' : 'Status'}
          </Typography>
          {getStatusChip(order.status)}
        </Box>
        
        <Box sx={{ minWidth: 100, textAlign: 'right' }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.7rem' }}>
            {language === 'ar' ? 'المبلغ' : 'Total'}
          </Typography>
          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {formatPrice(order.total_amount, order.currency)}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            deleteOrder(order.id);
          }}
          sx={{ color: '#F44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}
        >
          <DeleteIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <ArrowRightIcon sx={{ color: '#FFD700' }} />
      </Box>
    </Paper>
  );

  // Empty state
  const EmptyOrders = () => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <ReceiptIcon sx={{ fontSize: 80, color: '#2D2D2D', mb: 2 }} />
      <Typography variant="h5" sx={{ color: '#B3B3B3', mb: 1 }}>
        {language === 'ar' ? 'لا توجد طلبات' : 'No orders found'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B6B6B' }}>
        {language === 'ar' 
          ? 'لم تقم بأي طلب حتى الآن. ابدأ بالتسوق الآن!'
          : "You haven't placed any orders yet. Start shopping now!"}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate('/')}
        sx={{
          mt: 3,
          backgroundColor: '#FFD700',
          color: '#000000',
          '&:hover': { backgroundColor: '#FFE55C' }
        }}
      >
        {language === 'ar' ? 'بدء التسوق' : 'Start Shopping'}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ 
      py: { xs: 2, sm: 4 }, 
      mb: 10,
      minHeight: '100vh',
      backgroundColor: '#000000'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/')}
            sx={{ 
              color: '#FFD700',
              '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ 
            color: '#FFD700',
            fontWeight: 800,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            {language === 'ar' ? 'طلباتي' : 'My Orders'}
          </Typography>
        </Box>
        
        {orders.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={clearAllOrders}
            sx={{
              borderColor: '#F44336',
              color: '#F44336',
              '&:hover': {
                borderColor: '#D32F2F',
                backgroundColor: 'rgba(244, 67, 54, 0.1)'
              }
            }}
          >
            {language === 'ar' ? 'مسح الكل' : 'Clear All'}
          </Button>
        )}
      </Box>

      {/* Stats Summary */}
      {orders.length > 0 && (
        <Grow in>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                borderRadius: '12px'
              }}>
                <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>
                  {orders.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                borderRadius: '12px'
              }}>
                <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                  {orders.filter(o => o.status === 'delivered').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'تم التسليم' : 'Delivered'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                borderRadius: '12px'
              }}>
                <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 700 }}>
                  {orders.filter(o => o.status === 'processing' || o.status === 'pending').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'قيد المعالجة' : 'Processing'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#0D0D0D',
                border: '1px solid #2D2D2D',
                borderRadius: '12px'
              }}>
                <Typography variant="h4" sx={{ color: '#F44336', fontWeight: 700 }}>
                  {orders.filter(o => o.status === 'canceled').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'ملغية' : 'Canceled'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grow>
      )}

      {/* Orders List */}
      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2, bgcolor: '#1A1A1A', borderRadius: 2 }} />
          ))}
        </Box>
      ) : orders.length === 0 ? (
        <EmptyOrders />
      ) : (
        <Fade in>
          <Box>
            {isMobile ? (
              // Mobile view: Cards
              orders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              // Desktop view: Rows
              orders.map((order) => (
                <OrderRow key={order.id} order={order} />
              ))
            )}
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default UserOrdersListPage;